import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FormikProvider, useFormik } from "formik";
import Modal from "react-modal";
import Sheet from "react-modal-sheet";
import { BannerProps, Select, TextButton } from "kubra-ux-forge";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import XButton from "assets/icons/x_button.svg";
import { LOOKUP_NAMESPACE } from "constants/i18n-namespaces";
import { Navigate, useNavigate } from "react-router";
import { getDomain, getLabelWithKrn } from "utilities/helper";
import { useLabelsWithKrn } from "hooks";
import { LookupInfoImageModal } from "components/ezpay/common/lookup-info-image-modal";
import {
  postLookups,
  postPayableInitiateIntent,
  postPayableUpdate,
} from "api/IamApi/entities/ezpay";
import routing from "routing";
import { useDispatch, useSelector } from "react-redux";
import IamApi from "api/IamApi";
import { PayableType } from "types/payable";
import { setPayableSession } from "actions/payableSessionActions";
import { IContactInfo } from "types/biller";
import { ACTION } from "actions/paymentIntentActions";
import { getDeviceFingerprint, getIsMobile } from "selectors/appState";
import { generateValidationSchema } from "components/extension-fields/schema";
import { Field } from "components/extension-fields/Field";
import { EMPTY_WALLET_ITEMS } from "actions/walletActions";
import ContinueButton from "../continue-button/continue-button";

// Reusable code identifier for multiple accounts lookup condition
const MULTIPLE_ACCOUNTS = "MULTIPLE_ACCOUNTS";
// Field name constant for customer address composite input
const CUSTOMER_ADDRESS = "customerAddress";

export type CustomerAddress = {
  streetAddress: string;
  postalCode: string;
  locality: string;
  region: string;
};

export interface LookupsFormProps {
  setBannerState: (state: BannerProps) => void;
  biller: any;
  selectedPayableType: PayableType;
  selectedLookupError: boolean;
  setSelectedLookupError: (isError: boolean) => void;
}

export const LookupsForm: React.FC<LookupsFormProps> = (props) => {
  const fingerprint = useSelector(getDeviceFingerprint);
  const isMobile = useSelector(getIsMobile);
  const { setBannerState, biller } = props;
  const { t, i18n } = useTranslation([LOOKUP_NAMESPACE]);

  const selectedPayableType = props.selectedPayableType;

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedLookup, setSelectedLookup] = useState("");
  const [selectedLookupImage, setSelectedLookupImage] = useState("");
  const navigate = useNavigate();
  const domain = getDomain();
  const { getLabelsWithKrn } = useLabelsWithKrn();
  const labelSourceWithKrn = getLabelsWithKrn();
  const dispatch = useDispatch();

  const {
    name: payableTypeName,
    typeKrn,
    lookupFields,
    lookups,
  } = selectedPayableType;

  // Clear the session whenever we land on either the lookups page or payable type selection page.
  useEffect(() => {
    dispatch({ type: ACTION.RESET_PAYMENT_INTENT_STATE });
    dispatch({ type: EMPTY_WALLET_ITEMS });
    dispatch(setPayableSession(undefined));

    // This effect only needs to run once on initial mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lookupOptions = useMemo(() => {
    if (!lookups || lookups.length === 0) {
      return [];
    }

    return lookups.map((lookup) => {
      const formattedFields = lookup.fields
        .map((field) => {
          return getLabelWithKrn(
            labelSourceWithKrn.labelsWithKrn,
            typeKrn,
            field,
          );
        })
        .join(", ")
        .replace(/, ([^,]*)$/, ` ${t("and")} $1`);

      return {
        name: formattedFields,
        value: lookup.fields.join("_"),
        image: lookup.whereIsLookupInfoImg,
      };
    });
  }, [t, lookups, labelSourceWithKrn.labelsWithKrn, typeKrn]);

  const openModal = useCallback(() => setModalIsOpen(true), []);
  const closeModal = useCallback(() => {
    setModalIsOpen(false);
    setTimeout(() => document.body.style.removeProperty("overflow"), 50);
  }, []);

  const filteredLookupFields = useMemo(() => {
    if (lookups?.length === 1) {
      setSelectedLookup(lookups[0].fields.join("_"));

      lookups[0].whereIsLookupInfoImg &&
        setSelectedLookupImage(lookups[0].whereIsLookupInfoImg);
    }
    const selectedFields = selectedLookup?.split("_");
    return lookupFields
      ? lookupFields
          .filter((field) => selectedFields?.includes(field.name))
          .sort(
            (a, b) =>
              selectedFields?.indexOf(a.name) - selectedFields?.indexOf(b.name),
          )
      : [];
  }, [selectedLookup, lookups, lookupFields]);

  const contactUs = biller?.properties?.find((element: any) => {
    return element.name === "InstanceConfiguration";
  }) as IContactInfo;

  const validateLookups = async (spaceKrn: string, lookupsRequest: unknown) => {
    setLoading(true);

    try {
      const lookupResponse = await postLookups(spaceKrn, lookupsRequest);

      let nextPage: string | undefined = undefined;

      // Special case: If multiple results returned for account payables, throw an error
      // and show the validation banner.
      if (
        lookupResponse.data.length > 1 &&
        selectedPayableType?.category.toLowerCase() === "account"
      ) {
        throw {
          code: MULTIPLE_ACCOUNTS,
          message: "Multiple accounts found for account-type payable.",
        };
      }

      if (lookupResponse.data?.length > 1) {
        nextPage = routing.invoiceList.index();
      } else if (
        lookupResponse.data.length === 1 &&
        selectedPayableType?.extensionFields?.length > 0
      ) {
        const singlePayable = lookupResponse.data[0];
        await postPayableUpdate(singlePayable.krn);

        nextPage = routing.payableDetails.index();
      } else {
        // If single payable and no extension fields, then initiate the payment intent.
        const singlePayable = lookupResponse.data[0];
        await postPayableInitiateIntent(
          singlePayable.krn,
          fingerprint,
          navigator.userAgent,
        );

        nextPage = routing.payableDashboard.index();
      }

      const updatedSession = await IamApi.ezpay.getPayableFromSession(domain);
      dispatch(setPayableSession(updatedSession));

      navigate(nextPage);
    } catch (error: any) {
      let messageLabel = t("generic-lookup-error");
      let messageBody = undefined;

      if (
        (error.code === MULTIPLE_ACCOUNTS || error.errorMessageCode === 404) &&
        selectedPayableType?.category.toLowerCase() === "account"
      ) {
        messageLabel = t("invalid-entry");
        messageBody = t("validation-error", {
          billedName: biller?.billerName,
          contactNo: contactUs.properties.phone,
        });
      } else if (error.errorMessageCode === 404) {
        messageLabel = t("payable-lookup-error", {
          payableName: getLabelWithKrn(
            labelSourceWithKrn.labelsWithKrn,
            typeKrn,
            selectedPayableType.name,
          ),
        });
        messageBody = undefined;
      }

      setBannerState({
        active: true,
        variant: "error",
        messageLabel,
        messageBody,
        withClose: true,
        withIcon: true,
        withHyperlink: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: (lookupFields ?? []).reduce<Record<string, any>>(
      (values, field) => {
        values[field.name] = "";
        return values;
      },
      { selectedLookup: "" },
    ),
    validationSchema: generateValidationSchema(
      filteredLookupFields,
      t,
      labelSourceWithKrn.labelsWithKrn,
      typeKrn,
    ),
    onSubmit: (values) => {
      const { selectedLookup, ...filteredValues } = values;
      const transformedEntries = Object.entries(filteredValues).map(
        ([key, value]) => {
          if (key === CUSTOMER_ADDRESS && value && typeof value === "object") {
            const { streetAddress, postalCode, locality, region } =
              value as CustomerAddress;
            // Flatten the structured CustomerAddress into a single string for the API payload.
            // Backend expects the lookup value as: "streetAddress postalCode locality region country" (space-delimited).
            // Temporary: append "US" as the country until UX finalizes the country field in the Address UI.
            // We omit empty parts to avoid extra spaces while preserving the required order.
            const parts = [
              streetAddress,
              postalCode,
              locality,
              region,
              "US",
            ].filter((v) => !!v && String(v).trim() !== "");
            return [key, parts.join(" ")];
          }
          return [key, value];
        },
      );

      const lookups = Object.fromEntries(
        transformedEntries.filter(
          ([_, value]) => value !== "" && value !== null && value !== undefined,
        ),
      );
      const lookupsFields = {
        typeKrn: typeKrn,
        lookupFields: { ...lookups },
        domain: domain,
        channel: "Web",
        language: i18n.language,
      };

      !props.selectedLookupError &&
        validateLookups(biller.spaceKrn, lookupsFields);
    },
  });

  useEffect(() => {
    formik.validateForm();
    formik.setTouched({});
    setSelectedLookup("");
    setSelectedLookupImage("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  if (!lookups || lookups.length === 0) {
    console.error("No lookups available");
    return <Navigate to={routing.index()} />;
  }

  const handleContinueClick = () => {
    if (lookups.length > 1 && !selectedLookup) {
      props.setSelectedLookupError(true);
      return;
    }
    props.setSelectedLookupError(false);
    formik.handleSubmit();
  };

  return (
    <div>
      {isMobile ? (
        <Sheet
          isOpen={modalIsOpen}
          onClose={closeModal}
          detent="content-height"
          aria-labelledby="information-modal-title-mobile"
          disableDrag
        >
          <Sheet.Container>
            <Sheet.Header>
              <div className="react-modal-sheet-header heading-style-override">
                <h2
                  id="information-modal-title-mobile"
                  className="help-modal-header-text"
                >
                  {t("information")}
                </h2>
                <button className="modal-close" onClick={closeModal}>
                  <img
                    src={XButton}
                    alt={t("close-modal")}
                    className="modal-close-x"
                  />
                </button>
              </div>
            </Sheet.Header>
            <Sheet.Content>
              <LookupInfoImageModal
                payableTypeName={getLabelWithKrn(
                  labelSourceWithKrn.labelsWithKrn,
                  typeKrn,
                  payableTypeName,
                )}
                whereIsLookupInfoImg={selectedLookupImage}
              />
            </Sheet.Content>
          </Sheet.Container>
          <Sheet.Backdrop onTap={closeModal} />
        </Sheet>
      ) : (
        <Modal
          isOpen={modalIsOpen}
          ariaHideApp={false}
          aria={{
            labelledby: "information-modal-title",
          }}
          className="modal-frame-help"
          shouldCloseOnOverlayClick
          onRequestClose={closeModal}
        >
          <div className="modal-header heading-style-override">
            <h2
              id="information-modal-title"
              className="help-modal-header-text"
            >
              {t("information")}
            </h2>
            <button className="modal-close" onClick={closeModal}>
              <img
                src={XButton}
                alt={t("close-modal")}
                className="modal-close-x"
              />
            </button>
          </div>
          <LookupInfoImageModal
            payableTypeName={getLabelWithKrn(
              labelSourceWithKrn.labelsWithKrn,
              typeKrn,
              payableTypeName,
            )}
            whereIsLookupInfoImg={selectedLookupImage}
          />
        </Modal>
      )}

      <FormikProvider value={formik}>
        <form
          onSubmit={formik.handleSubmit}
          key={i18n.language}
          data-testid="lookups-form"
          className="form-container"
        >
          <div className="ez-pay-payable-row">
            <div className="center-text">
              {t("lookup-info", {
                lookupName: getLabelWithKrn(
                  labelSourceWithKrn.labelsWithKrn,
                  typeKrn,
                  payableTypeName,
                ),
              })}
            </div>
          </div>
          {lookups.length > 1 && (
            <div className="ez-pay-payable-row">
              <label
                htmlFor={`select${payableTypeName}`}
                className="validate-label"
              >
                {t("lookup-label", {
                  lookupName: getLabelWithKrn(
                    labelSourceWithKrn.labelsWithKrn,
                    typeKrn,
                    payableTypeName,
                  ),
                })}
              </label>
              <Select
                name="selectedLookup"
                label={""}
                onChange={(option: { id: string; image: string }[]) => {
                  formik.resetForm();
                  props.setSelectedLookupError(false);
                  setSelectedLookup(option[0]?.id);
                  setSelectedLookupImage(option[0]?.image);
                }}
                placeholder={t("select-lookup-field")}
                options={lookupOptions.map((lookup) => ({
                  id: lookup?.value,
                  name: getLabelWithKrn(
                    labelSourceWithKrn.labelsWithKrn,
                    typeKrn,
                    lookup?.name,
                  ),
                  image: lookup?.image,
                }))}
                hasError={
                  formik.touched.selectedLookup &&
                  !!formik.errors.selectedLookup
                }
                canDeselect={false}
              />
            </div>
          )}

          {filteredLookupFields.map((field) => (
            <Field key={field.name} fieldDefinition={field} typeKrn={typeKrn} />
          ))}

          {selectedLookupImage && (
            <div className="ez-pay-payable-row">
              <TextButton
                onClick={openModal}
                className="account-modal-link"
                hoverStyle="underline"
                icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
              >
                <div className="account-modal-link-label">
                  {t("where-to-find-lookup-details", {
                    lookupName: getLabelWithKrn(
                      labelSourceWithKrn.labelsWithKrn,
                      typeKrn,
                      payableTypeName,
                    ),
                  })}
                </div>
              </TextButton>
            </div>
          )}

          <ContinueButton
            loading={loading}
            onClick={() => handleContinueClick()}
          />
        </form>
      </FormikProvider>
    </div>
  );
};

export default LookupsForm;
